import { useEffect, useState } from 'react';

import config from '@/config';
import useStateHandler from '@/hooks/useStateHandler';
import GalleryService from '@/services/gallery.service';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import PublicFormInput from '@/shared-components/inputs/components/PublicFormInput';
import Utils from '@/utils';

import ArtistDeceased from './components/ArtistDeceased';
import FormText from './components/FormText';
import UnknownBirth from './components/UnknownBirth';

import './index.css';

export default function ArtistForm({ artistData, onCreate, createMode, editMode, close }) {
  const { cacheHandler, stateHandler } = useStateHandler();

  const [formState, setFormState] = useState(Utils.initArtistForm(artistData));
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState(false);

  // 🔒 Bloqueo de email si viene con "artist.suarte.art"
  const [emailLocked, setEmailLocked] = useState(false);
  const BLOCKED_EMAIL_SNIPPET = 'artist.suarte.art';

  useEffect(() => {
    const rawEmail = (artistData?.artist_email || '').toLowerCase();
    if (rawEmail.includes(BLOCKED_EMAIL_SNIPPET)) {
      setEmailLocked(true);
      // Vaciar el campo visualmente en el formulario
      setFormState(prev => ({ ...prev, artist_email: '' }));
    }

  }, []); // solo al montar

  // --- helpers de estado ---
  function updateFormState(key, value) {
    setFormState(prev => Utils.addAttributeToObject(key, value, prev));
  }

  function toggleBirthUnknown(value) {
    setFormState((prev) => ({
      ...prev,
      isBirthUnknown: value,
      artist_birth: value ? "Unknown" : "",
    }));
  }

  function toggleDeathUnknown(value) {
    setFormState((prev) => ({
      ...prev,
      isDeathUnknown: value,
      artist_death: value ? "Unknown" : "",
    }));
  }

  function canSubmit() {
    // allow “Unknown” literal or 4-digit number
    const birthOk =
      formState.artist_birth === "Unknown" ||
      /^[0-9]{4}$/.test(formState.artist_birth);

    const base =
      Boolean(formState.artist_name) &&
      Boolean(formState.artist_nationality) &&
      birthOk &&
      Boolean(
        !formState.isArtistDeceased ||
          formState.artist_death === "Unknown" ||
          /^[0-9]{4}$/.test(formState.artist_death)
      );

    // Requerir email salvo que esté bloqueado por "artist.suarte.art"
    const mustHaveEmail = (editMode || createMode === "Work of Artist") && !emailLocked;

    return base && (mustHaveEmail ? Boolean(formState.artist_email) : true);
  }

  async function submitArtistForm(e) {
    e.preventDefault();
    if (!canSubmit()) {
      setFormError({ element: "artist_birth", error_code: "invalid_birth_year" });
      return;
    }

    setLoading(true);
    setFormError(null);

    if (editMode) {
      const { response, data } = await GalleryService.editArtistProfile(formState);
      if (response.ok) {
        stateHandler.set("temporalPopup", {
          text: "Artist information updated successfully!",
        });
        onCreate(data);
        close();
      } else if (response.status === 400) {
        setLoading(false);
        setFormError({
          element: data.error_data.element,
          error_code: data.error_type,
        });
      }
    } else {
      const { response, data } = await GalleryService.createArtistProfile(
        formState,
        createMode
      );
      if (response.ok) {
        onCreate(data);
        cacheHandler.triggerAction("NEW_ARTIST");
        close();
      } else if (response.status === 400) {
        setLoading(false);
        setFormError({
          element: data.error_data.element,
          error_code: data.error_type,
        });
      }
    }
  }

  return (
    <form onSubmit={submitArtistForm} className="artist-form__container" autoComplete="off">
      <PublicFormInput
        readOnly={loading}
        error={formError}
        placeholder="Name"
        element="artist_name"
        autoComplete="off"
        value={formState.artist_name}
        onChange={(e) => updateFormState("artist_name", e.target.value)}
        margin
      />

      {createMode !== "Gallery owned" && (
        <PublicFormInput
          // ⛔ Bloqueado si viene del sistema con artist.suarte.art
          readOnly={loading || emailLocked}
          disabled={loading || emailLocked}
          error={formError}
          type="email"
          placeholder="Email of the artist"
          element="artist_email"
          name="artist_contact"
          autoComplete="new-password"
          value={emailLocked ? "" : formState.artist_email}
          onChange={(e) =>
            updateFormState("artist_email", e.target.value?.toLowerCase())
          }
          inputProps={{
            title: emailLocked
              ? "This email is managed by Suarte. Replace it later with the artist’s real email."
              : undefined,
          }}
          margin
        />
      )}

      <PublicFormInput
        readOnly={loading}
        error={formError}
        selectOptions={{
          options: config.forms.prefix_options,
          type: "country",
          searchable: true,
        }}
        placeholder="Nationality"
        type="select"
        element="artist_nationality"
        autoComplete="off"
        value={formState.artist_nationality}
        onChange={(value) => updateFormState("artist_nationality", value)}
        margin
      />

      {/* Year of birth field */}
      <PublicFormInput
        readOnly={loading}
        error={formError}
        placeholder="Year of birth"
        element="artist_birth"
        autoComplete="off"
        value={formState.artist_birth}
        onChange={(e) => {
          const digits = e.target.value.replace(/\D/g, '').slice(0, 4);
          updateFormState("artist_birth", digits);
        }}
        inputProps={{ maxLength: 4 }}
        margin
        disabled={formState.isBirthUnknown}
        className={formState.isBirthUnknown ? "faded" : ""}
      />

      {/* Unknown birth year checkbox */}
      <UnknownBirth
        loading={loading}
        isUnknown={formState.isBirthUnknown}
        onToggle={toggleBirthUnknown}
      />

      {/* Deceased checkbox & date */}
      <ArtistDeceased
        loading={loading}
        formState={formState}
        updateFormState={updateFormState}
        toggleDeathUnknown={toggleDeathUnknown}
      />

      {(!editMode && createMode !== "Gallery owned") && (
        <FormText formState={formState} createMode={createMode}/>
      )}

      <ContinueButton
        turnOff={!canSubmit()}
        loading={loading}
        className="artist-form__submit-button font-size-medium"
      >
        {editMode ? "Save changes" : "Create profile"}
      </ContinueButton>
    </form>
  );
}
